import React, { useEffect, useRef } from 'react';
import { Container, Stack } from '@fiverr-private/layout_components';
import { Button } from '@fiverr-private/button';
import { I18n } from '@fiverr-private/i18n-react';

export interface EditWrapperProps {
  isEditing: boolean;
  viewMode: React.ReactNode;
  editMode: React.ReactNode;
  onCancel: () => void;
  onEditEnd: () => void;
  hideActions?: boolean;
  isDisabled?: boolean;
  shouldRemoveSection?: boolean;
  disableInitialScroll?: boolean;
  onScrolledToEdit?: () => void;
}

export const EditWrapper = ({
  isEditing,
  viewMode,
  editMode,
  onEditEnd,
  onCancel,
  hideActions,
  isDisabled,
  shouldRemoveSection,
  disableInitialScroll = false,
  onScrolledToEdit,
}: EditWrapperProps) => {
  const editModeRef = useRef<HTMLElement>(null);
  const hasScrolledRef = useRef(false);

  useEffect(() => {
    if (isEditing && !disableInitialScroll && !hasScrolledRef.current) {
      editModeRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'center',
      });
      hasScrolledRef.current = true;
      onScrolledToEdit?.();
    }
  }, [isEditing, disableInitialScroll, onScrolledToEdit]);

  useEffect(() => {
    if (!isEditing) {
      hasScrolledRef.current = false;
    }
  }, [isEditing]);

  if (!isEditing) {
    return <Container>{viewMode}</Container>;
  }

  return (
    <Container ref={editModeRef}>
      {editMode}
      {!hideActions && (
        <Stack alignItems="center" justifyContent="flexEnd" paddingTop="2" gap="2">
          <Button intent="secondary" size="sm" variant="ghost" onClick={onCancel}>
            <Container paddingX="3" paddingY="0.5">
              <I18n k={`ai_brief.edit_brief.${shouldRemoveSection ? 'remove' : 'cancel'}`} />
            </Container>
          </Button>
          <Button disabled={isDisabled} intent="primary" size="sm" variant="outline" onClick={onEditEnd}>
            <Container paddingX="3" paddingY="0.5">
              <I18n k="ai_brief.edit_brief.save" />
            </Container>
          </Button>
        </Stack>
      )}
    </Container>
  );
};
